import type { FlowState, FlowType, FlowView } from 'pages/organizationFlow/flowStorage.data';
import type { CsvValidatorDataRow } from 'pages/organizationFlow/type.data';

export type { FlowState, FlowType, FlowView } from 'pages/organizationFlow/flowStorage.data';

const STORAGE_KEY_PREFIX = 'organizationFlowState';

/**
 * Returns the storage key for the flow state.
 * Use a suffix (e.g. userId or orgId) when you need to isolate state per user/org on the same device.
 */
export function getFlowStorageKey(suffix?: string): string {
  return suffix ? `${STORAGE_KEY_PREFIX}_${suffix}` : STORAGE_KEY_PREFIX;
}

const DEFAULT_STATE: FlowState = {
  view: 'login',
  organizationName: '',
  units: [],
  unitRows: [],
  validatorRows: [],
  selectedUnit: '',
  flowType: 'existing',
  payrollValidationResult: undefined,
  planValidationResult: undefined,
  lastPayrollResultByUnit: undefined,
  unitCsvUploadStatus: 'idle',
};

const VALID_VIEWS: FlowView[] = [
  'select',
  'create',
  'setup',
  'reviewUnits',
  'selectUnit',
  'completeSetup',
  'reviewPayrollPlan',
  'reviewUnit',
  'connectPayrollProvider',
];

const VALID_FLOW_TYPES: FlowType[] = ['create', 'existing'];
const VALID_UPLOAD_STATUSES: FlowState['unitCsvUploadStatus'][] = [
  'incomplete',
  'failed',
  'success',
];

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function normalizeArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function normalizeObject<T>(value: unknown): T | undefined {
  return value && typeof value === 'object' ? (value as T) : undefined;
}

function normalizeFlowType(value: unknown): FlowType {
  return typeof value === 'string' && VALID_FLOW_TYPES.includes(value as FlowType)
    ? (value as FlowType)
    : 'existing';
}

function normalizeUploadStatus(value: unknown): FlowState['unitCsvUploadStatus'] {
  return typeof value === 'string' &&
    VALID_UPLOAD_STATUSES.includes(value as FlowState['unitCsvUploadStatus'])
    ? (value as FlowState['unitCsvUploadStatus'])
    : 'idle';
}

function resolveStoredView(view: unknown): FlowView | null {
  if (typeof view !== 'string' || !VALID_VIEWS.includes(view as FlowView)) {
    return null;
  }

  return view as FlowView;
}

function buildStoredFlowState(parsed: Partial<FlowState>): FlowState | null {
  // Do not restore to login/otpVerification from storage (security)
  const view = resolveStoredView(parsed.view);
  if (!view) return null;

  return {
    view,
    organizationName: normalizeString(parsed.organizationName),
    units: normalizeArray(parsed.units),
    unitRows: normalizeArray(parsed.unitRows),
    validatorRows: normalizeArray<CsvValidatorDataRow>(parsed.validatorRows),
    selectedUnit: normalizeString(parsed.selectedUnit),
    flowType: normalizeFlowType(parsed.flowType),
    payrollValidationResult: normalizeObject(parsed.payrollValidationResult),
    planValidationResult: normalizeObject(parsed.planValidationResult),
    lastPayrollResultByUnit: normalizeObject(parsed.lastPayrollResultByUnit),
    unitCsvUploadStatus: normalizeUploadStatus(parsed.unitCsvUploadStatus),
  };
}

export const getStoredFlowState = (storageKey?: string): FlowState | null => {
  try {
    const key = storageKey ?? getFlowStorageKey();
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as Partial<FlowState>;
    return buildStoredFlowState(parsed);
  } catch {
    return null;
  }
};

export const saveFlowState = (state: FlowState, storageKey?: string): void => {
  try {
    const key = storageKey ?? getFlowStorageKey();
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // Ignore storage errors (e.g. private browsing, quota exceeded)
  }
};

export const clearFlowState = (storageKey?: string): void => {
  try {
    const key = storageKey ?? getFlowStorageKey();
    localStorage.removeItem(key);
  } catch {
    // Ignore storage errors
  }
};

export { DEFAULT_STATE };
